import { Query, PaginatedQueryResponse } from "../../api/OasisBackendApi";
import { useOasisBackend } from "../../hooks/useOasisBackend";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const QueryManagerTable = ({entityCollectionId, databaseId}: {entityCollectionId: string, databaseId: string}) => {
    const db = useOasisBackend();
    const [queries, setQueries] = useState<Query[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        count: 0,
        next: null as string | null,
        previous: null as string | null,
        currentPage: 1,
        pageSize: 50
    });

    // Fetch queries on component mount
    useEffect(() => {
        fetchQueries(1);
    }, [entityCollectionId, db.endpoints]);

    const fetchQueries = async (page: number = 1) => {
        try {
            setLoading(true);
            const response = await db.endpoints.api.apiEntityCollectionQueriesRetrieve(entityCollectionId, {
                page: page,
                page_size: pagination.pageSize
            });
            
            const paginatedData = response.data as PaginatedQueryResponse;
            setQueries(paginatedData.results);
            setPagination(prev => ({
                ...prev,
                count: paginatedData.count,
                next: paginatedData.next,
                previous: paginatedData.previous,
                currentPage: page
            }));
            setError(null);
        } catch (err) {
            setError("Failed to fetch queries");
            console.error("Error fetching queries:", err);
        } finally {
            setLoading(false);
        }
    };

    // Handle query deletion
    const handleDeleteQuery = async (queryId: string) => {
        try {
            await db.endpoints.api.apiQueriesDestroy(queryId);
            // Remove the deleted query from the local state
            setQueries(queries.filter(query => query.id !== queryId));
        } catch (err) {
            setError("Failed to delete query");
            console.error("Error deleting query:", err);
        }
    };

    if (loading) {
        return <div className="flex justify-center items-center p-4 text-gray-300">Loading queries...</div>;
    }

    if (error) {
        return <div className="text-red-400 p-4 bg-red-900/20 border border-red-700/50 rounded-md">{error}</div>;
    }

    const handlePageChange = (page: number) => {
        fetchQueries(page);
    };

    const totalPages = Math.ceil(pagination.count / pagination.pageSize);

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        const currentPage = pagination.currentPage;
        const maxPages = totalPages;
        
        // Always show first page
        pages.push(1);
        
        // Show pages around current page
        const start = Math.max(2, currentPage - 2);
        const end = Math.min(maxPages - 1, currentPage + 2);
        
        if (start > 2) {
            pages.push('...');
        }
        
        for (let i = start; i <= end; i++) {
            if (i > 1 && i < maxPages) {
                pages.push(i);
            }
        }
        
        if (end < maxPages - 1) {
            pages.push('...');
        }
        
        // Always show last page if there is more than one page
        if (maxPages > 1) {
            pages.push(maxPages);
        }
        
        return pages;
    };

    return (
        <div className="space-y-4">
            <div className="overflow-x-auto">
                <div className="flex flex-col gap-2 pb-4">
                    <h2 className="text-xl font-semibold text-gray-100">Query Manager</h2>
                    <p className="text-sm text-gray-400 mt-1">Queries with answers are used to test the accuracy of your dimensions. Fixing, adding or removing queries will lead to better results in your Oasis dimensions next time you reindex.</p>
                </div>
                <table className="min-w-full bg-gray-800 border border-gray-700 rounded-lg">
                <thead className="bg-gray-700">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider border-b border-gray-600">
                            Query Text
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider border-b border-gray-600">
                            Classified Entity Matches
                            <span className="text-xs text-green-400"> Perfect Match </span>
                            | <span className="text-xs text-cyan-400"> Good Match </span>
                            | <span className="text-xs text-blue-400"> Match </span>
                            | <span className="text-xs text-yellow-400"> Partial Match </span>
                            | <span className="text-xs text-white-400"> Irrelevant </span>
                            | <span className="text-xs text-red-400"> Contradiction </span>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider border-b border-gray-600">
                            Indexed
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider border-b border-gray-600">
                            Created
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider border-b border-gray-600">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                    {queries.length === 0 ? (
                        <tr>
                            <td colSpan={5} className="px-6 py-4 text-center text-gray-400">
                                No queries found
                            </td>
                        </tr>
                    ) : (
                        queries.map((query) => (
                            <tr key={query.id} className="hover:bg-gray-700 transition-colors duration-200">
                                <td className="px-6 py-4 whitespace-normal text-sm text-gray-200 border-b border-gray-700">
                                    <div className="max-w-xs truncate" title={query.text}>
                                        {query.text}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-normal text-sm text-gray-200 border-b border-gray-700">
                                    <div className="max-w-xs flex flex-wrap gap-2">
                                        {/* Perfect Match - Green */}
                                        <span className="text-green-300">
                                            {query.match_type_counts?.perfect_match || 0}
                                        </span> |
                                        {/* Good Match - Blue */}
                                        <span className="text-cyan-300">
                                            {query.match_type_counts?.good_match || 0}
                                        </span> |
                                        {/* Match - Cyan */}
                                        <span className="text-blue-300">
                                            {query.match_type_counts?.match || 0}
                                        </span> |
                                        {/* Partial Match - Yellow */}
                                        <span className="text-yellow-300">
                                            {query.match_type_counts?.partial_match || 0}
                                        </span> |
                                        {/* Irrelevant - Gray */}
                                        <span className="text-gray-400">
                                            {query.match_type_counts?.irrelevant || 0}
                                        </span> |
                                        {/* Contradiction - Red */}
                                        <span className="text-red-400">
                                            {query.match_type_counts?.contradiction || 0}
                                        </span>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200 border-b border-gray-700">
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                        query.indexed 
                                            ? 'bg-green-900/30 text-green-300 border border-green-700/50' 
                                            : 'bg-yellow-900/30 text-yellow-300 border border-yellow-700/50'
                                    }`}>
                                        {query.indexed ? 'Indexed' : 'Not Indexed'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200 border-b border-gray-700">
                                    {new Date(query.created_at).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 flex flex-col items-center gap-4 whitespace-nowrap text-sm font-medium border-b border-gray-700">
                                    <Link to={`/knowledge-base/${databaseId}?q=${query.text}&tab=test`} className="text-blue-400 hover:text-blue-300 bg-blue-900/20 hover:bg-blue-900/40 border border-blue-700/50 hover:border-blue-600/70 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200">Test Query</Link>
                                    <button
                                        onClick={() => handleDeleteQuery(query.id)}
                                        className="text-red-400 hover:text-red-300 bg-red-900/20 hover:bg-red-900/40 border border-red-700/50 hover:border-red-600/70 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200"
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            </div>
            
            {/* Pagination Controls */}
            {pagination.count > 0 && (
                <div className="flex items-center justify-between px-4 py-3 bg-gray-700 border-t border-gray-600 rounded-b-lg">
                    <div className="flex items-center text-sm text-gray-300">
                        <span>
                            Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1} to{' '}
                            {Math.min(pagination.currentPage * pagination.pageSize, pagination.count)} of{' '}
                            {pagination.count} results
                        </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={!pagination.previous}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                                pagination.previous
                                    ? 'text-gray-300 hover:text-white hover:bg-gray-600 border border-gray-600 hover:border-gray-500'
                                    : 'text-gray-500 cursor-not-allowed'
                            }`}
                        >
                            Previous
                        </button>
                        
                        {/* Page Number Buttons */}
                        <div className="flex items-center space-x-1">
                            {getPageNumbers().map((page, index) => (
                                <div key={index}>
                                    {page === '...' ? (
                                        <span className="px-2 py-1.5 text-sm text-gray-400">...</span>
                                    ) : (
                                        <button
                                            onClick={() => handlePageChange(page as number)}
                                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                                                page === pagination.currentPage
                                                    ? 'bg-blue-600 text-white border border-blue-500'
                                                    : 'text-gray-300 hover:text-white hover:bg-gray-600 border border-gray-600 hover:border-gray-500'
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        
                        <button
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={!pagination.next}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                                pagination.next
                                    ? 'text-gray-300 hover:text-white hover:bg-gray-600 border border-gray-600 hover:border-gray-500'
                                    : 'text-gray-500 cursor-not-allowed'
                            }`}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QueryManagerTable;